import { computed, inject } from "@angular/core";
import { patchState, signalStore, withComputed, withMethods, withProps, withState } from "@ngrx/signals";
import { DocumentService } from "../../services/document/document.service";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { concatMap, pipe, tap } from "rxjs";
import { tapResponse } from "@ngrx/operators";
import { setEntities, withEntities } from "@ngrx/signals/entities";
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper";
import { UserFacade } from "../user/user.facade";

interface DocumentCenterItem {
    id: number;
    provider_id?: number;
    document_type_id: number;
    document_type_name?: string;
    bucket_key: string;
    object_key: string;
    active: boolean;
    created_at: string;
    updated_at: string;
    ended_at: string | null;
    title: string | null;
    description: string | null;
    view_count: number;
    presigned_url?: string;
    document_link?: string;
}

const PROVIDER_DOCUMENT_TYPE_IDS = [1, 2, 3, 4, 9];
const MEMBER_DOCUMENT_TYPE_IDS = [10, 12, 13];

function extractDocuments(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.documents)) return response.documents;
    if (Array.isArray(response?.result)) return response.result;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result?.documents)) return response.result.documents;
    if (Array.isArray(response?.data?.documents)) return response.data.documents;
    return [];
}

function normalizeDocuments(response: any): DocumentCenterItem[] {
    return extractDocuments(response).map((d: any) => ({
        ...d,
        id: d.id ?? d.document_id,
        active: d.active === undefined || d.active === null || d.active === true || d.active === 1 || d.active === '1' || d.active === 'true',
        title: d.title ?? d.document_type_name ?? null,
        description: d.description ?? null,
        presigned_url: d.presigned_url ?? d.document_link,
        document_link: d.document_link ?? d.presigned_url
    }));
}

function isActiveDocument(document: DocumentCenterItem): boolean {
    return document.active === true;
}

export const DocumentCenterStore = signalStore(
    { providedIn: 'root' },
    withState({
        documentCenterStatus: 'idle' as 'idle' | 'loading' | 'success' | 'error',
        error: {
            error: false,
            message: ''
        }
    }),
    withEntities<DocumentCenterItem>(),
    withProps(() => ({
        documentService: inject(DocumentService),
        userFacade: inject(UserFacade)
    })),
    withComputed((store) => ({
        providerSpecificDocuments: computed(() =>
            store.entities().filter((d) =>
                isActiveDocument(d) &&
                d.provider_id === store.userFacade.provider_id() &&
                PROVIDER_DOCUMENT_TYPE_IDS.includes(d.document_type_id)
            )
        ),
        providerOnlyDocuments: computed(() =>
            store.entities().filter((d) =>
                isActiveDocument(d) &&
                PROVIDER_DOCUMENT_TYPE_IDS.includes(d.document_type_id)
            )
        ),
        memberOnlyDocuments: computed(() =>
            store.entities().filter((d) =>
                isActiveDocument(d) &&
                MEMBER_DOCUMENT_TYPE_IDS.includes(d.document_type_id)
            )
        ),
        generalUseDocuments: computed(() =>
            store.entities().filter((d) => isActiveDocument(d) && d.document_type_id === 11)
        )
    })),
    withMethods((store) => ({
        setDocumentCenterStatus(documentCenterStatus: 'idle' | 'loading' | 'success' | 'error') {
            patchState(store, { documentCenterStatus });
        },
        setDocumentCenterError(message: string) {
            patchState(store, {
                error: {
                    error: true,
                    message
                }
            });
        },
        getProviderDocumentsByProviderId(providerId: number) {
            return store.entities().filter((d) =>
                isActiveDocument(d) &&
                d.provider_id === providerId &&
                PROVIDER_DOCUMENT_TYPE_IDS.includes(d.document_type_id)
            );
        }
    })),
    withMethods((store) => ({
        getDocuments: rxMethod<number>(
            pipe(
                tap(() => {
                    store.setDocumentCenterStatus('loading');
                }),
                concatMap((document_type_id) =>
                    store.documentService.getAdminDocuments({ document_type_id }).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const documents = normalizeDocuments(response);
                                patchState(store, setEntities(documents));
                                store.setDocumentCenterStatus('success');
                            },
                            error: (err: unknown) => {
                                store.setDocumentCenterStatus('error');
                                store.setDocumentCenterError(extractErrorMessage(err));
                            }
                        })
                    )
                )
            )
        ),

        getDocumentsByProvider: rxMethod<void>(
            pipe(
                tap(() => {
                    store.setDocumentCenterStatus('loading');
                }),
                concatMap(() =>
                    store.documentService.getDocumentsByProvider(
                        store.userFacade.clinic_id(),
                        store.userFacade.provider_id()
                    ).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const documents = normalizeDocuments(response);
                                patchState(store, setEntities(documents));
                                store.setDocumentCenterStatus('success');
                            },
                            error: (err: unknown) => {
                                store.setDocumentCenterStatus('error');
                                store.setDocumentCenterError(extractErrorMessage(err));
                            }
                        })
                    )
                )
            )
        ),

        getDocumentsByMember: rxMethod<number>(
            pipe(
                tap(() => {
                    store.setDocumentCenterStatus('loading');
                }),
                concatMap((member_id) =>
                    store.documentService.getDocumentsByMember(member_id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                const documents = normalizeDocuments(response);
                                patchState(store, setEntities(documents));
                                store.setDocumentCenterStatus('success');
                            },
                            error: (err: unknown) => {
                                store.setDocumentCenterStatus('error');
                                store.setDocumentCenterError(extractErrorMessage(err));
                            }
                        })
                    )
                )
            )
        )
    }))
);
